import ExcelJS from 'exceljs';

export type AnnualOperationPlanExportWeekday = {
  label: string;
};

export type AnnualOperationPlanExportClassHeader = {
  columnKey: string;
  gradeNumber: string;
};

export type AnnualOperationPlanExportDayCell = {
  value: string;
  inTerm: boolean;
  dayKind: 'class' | 'holiday' | 'closure' | 'vacation';
  fixedDayGeneralMemoOnly: boolean;
  fixedDayTextColor?: string;
  hasFixedEvents: boolean;
};

export type AnnualOperationPlanExportClassHourCell = {
  value: number | string;
  adjusted: boolean;
};

export type AnnualOperationPlanExportActivityLine = {
  datePart: string;
  tagCommon: boolean;
  gradeNums: number[];
  name: string;
  kind: 'calendar' | 'fixed' | 'shortday';
  colorHex?: string;
  isGeneralMemo?: boolean;
};

export type AnnualOperationPlanExportRow = {
  rowKind?: 'week' | 'subtotal' | 'grandTotal';
  monthLabel: string;
  weekIndex: number | string;
  instructionalDayCount: number | string;
  weekdayCells: AnnualOperationPlanExportDayCell[];
  classHourCells: AnnualOperationPlanExportClassHourCell[];
  activityLines: AnnualOperationPlanExportActivityLine[];
  remarkTexts: string[];
  footerLabel?: string;
  vacationText?: string;
};

const EXCEL_COLORS = {
  headerBg: 'FFF4F4F4',
  totalBg: 'FFF0F0F0',
  footerTerm1Bg: 'FFE8D4DC',
  footerTerm2Bg: 'FFD8E6EF',
  footerGrandTotalBg: 'FFFFEFB8',
  dayInactiveBg: 'FFE8ECF1',
  dayHolidayBg: 'FFF0C4C4',
  dayClosureBg: 'FFFFDFC4',
  dayMemoGeneralText: 'FF6B9590',
  hoursAdjustedText: 'FF4CC7AE',
  activityCalendarText: 'FFD32F2F',
  activityShortdayText: 'FF4CC7AE',
  activityMemoGeneralText: 'FF6B9590',
  defaultText: 'FF111111',
  highlightYellowBg: '1AFFEC8C',
} as const;

function solidCellBorder(): Partial<ExcelJS.Borders> {
  const side: ExcelJS.Border = { style: 'thin', color: { argb: 'FF000000' } };
  return {
    top: { ...side },
    left: { ...side },
    bottom: { ...side },
    right: { ...side },
  };
}

function solidFill(argb: string): ExcelJS.Fill {
  return {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb },
  };
}

function hexToArgb(hex: string | undefined, fallback = EXCEL_COLORS.defaultText): string {
  const trimmed = (hex ?? '').trim().replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(trimmed)) return fallback;
  return `FF${trimmed.toUpperCase()}`;
}

function displayWidthOfText(text: string): number {
  let width = 0;
  for (const ch of text) {
    width += ch.charCodeAt(0) > 255 ? 2 : 1;
  }
  return width;
}

function formatActivityTags(line: AnnualOperationPlanExportActivityLine): string {
  if (line.tagCommon) return '[공통]';
  return line.gradeNums.map((gradeNum) => `[${gradeNum}]`).join(' ');
}

function formatActivityLinePlain(line: AnnualOperationPlanExportActivityLine): string {
  const tags = formatActivityTags(line);
  return [line.datePart, tags, line.name].filter(Boolean).join(' ');
}

function formatRemarkText(text: string): string {
  return `[주] ${text}`;
}

function activityLineFont(line: AnnualOperationPlanExportActivityLine): Partial<ExcelJS.Font> {
  if (line.isGeneralMemo) {
    return { color: { argb: EXCEL_COLORS.activityMemoGeneralText }, bold: true };
  }
  if (line.kind === 'calendar') {
    return { color: { argb: EXCEL_COLORS.activityCalendarText }, bold: true };
  }
  if (line.kind === 'shortday') {
    return { color: { argb: EXCEL_COLORS.activityShortdayText }, bold: true };
  }
  if (line.kind === 'fixed') {
    return {
      color: { argb: hexToArgb(line.colorHex, EXCEL_COLORS.defaultText) },
      bold: true,
    };
  }
  return { color: { argb: EXCEL_COLORS.defaultText } };
}

function buildActivityRichText(lines: AnnualOperationPlanExportActivityLine[]): ExcelJS.CellRichTextValue {
  const richText: ExcelJS.RichText[] = [];
  lines.forEach((line, index) => {
    if (index > 0) {
      richText.push({ text: '\n', font: { color: { argb: EXCEL_COLORS.defaultText } } });
    }
    const lineFont = activityLineFont(line);
    richText.push({ text: `${line.datePart} `, font: { ...lineFont } });
    const tags = formatActivityTags(line);
    if (tags) {
      richText.push({ text: `${tags} `, font: { ...lineFont } });
    }
    richText.push({ text: line.name, font: { ...lineFont } });
  });
  return { richText };
}

function applyDayCellStyle(cell: ExcelJS.Cell, day: AnnualOperationPlanExportDayCell): void {
  styleBodyCell(cell, 'center');
  if (!day.inTerm) {
    cell.fill = solidFill(EXCEL_COLORS.dayInactiveBg);
    cell.font = { bold: true, color: { argb: EXCEL_COLORS.defaultText } };
    return;
  }
  if (day.dayKind === 'holiday') {
    cell.fill = solidFill(EXCEL_COLORS.dayHolidayBg);
    cell.font = { bold: true, color: { argb: EXCEL_COLORS.defaultText } };
    return;
  }
  if (day.dayKind === 'closure' || day.dayKind === 'vacation') {
    cell.fill = solidFill(EXCEL_COLORS.dayClosureBg);
    cell.font = { bold: true, color: { argb: EXCEL_COLORS.defaultText } };
    return;
  }
  if (day.fixedDayGeneralMemoOnly) {
    cell.font = { bold: true, color: { argb: EXCEL_COLORS.dayMemoGeneralText } };
    return;
  }
  if (day.hasFixedEvents && day.fixedDayTextColor) {
    cell.font = {
      bold: true,
      color: { argb: hexToArgb(day.fixedDayTextColor, EXCEL_COLORS.defaultText) },
    };
    return;
  }
  if (day.hasFixedEvents) {
    cell.font = { bold: true, color: { argb: EXCEL_COLORS.defaultText } };
  }
}

function triggerBrowserDownload(buffer: ArrayBuffer, fileName: string): void {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
  anchor.click();
  URL.revokeObjectURL(url);
}

function styleHeaderCell(cell: ExcelJS.Cell): void {
  cell.border = solidCellBorder();
  cell.font = { bold: true, color: { argb: EXCEL_COLORS.defaultText } };
  cell.fill = solidFill(EXCEL_COLORS.headerBg);
  cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
}

function applyCellChrome(cell: ExcelJS.Cell, align: 'center' | 'left' = 'center'): void {
  cell.border = solidCellBorder();
  cell.alignment = {
    horizontal: align,
    vertical: align === 'left' ? 'top' : 'middle',
    wrapText: true,
  };
}

function styleBodyCell(cell: ExcelJS.Cell, align: 'center' | 'left' = 'center'): void {
  applyCellChrome(cell, align);
  if (!cell.font) {
    cell.font = { color: { argb: EXCEL_COLORS.defaultText } };
  }
}

function styleTotalCell(
  cell: ExcelJS.Cell,
  align: 'center' | 'left' = 'center',
  backgroundArgb: string = EXCEL_COLORS.totalBg
): void {
  styleBodyCell(cell, align);
  cell.font = { ...(cell.font ?? {}), bold: true, color: { argb: EXCEL_COLORS.defaultText } };
  cell.fill = solidFill(backgroundArgb);
}

function resolveFooterBackgroundArgb(footer: {
  kind?: 'subtotal' | 'grandTotal';
  termNumber?: 1 | 2;
}): string {
  if (footer.termNumber === 1) return EXCEL_COLORS.footerTerm1Bg;
  if (footer.termNumber === 2) return EXCEL_COLORS.footerTerm2Bg;
  if (footer.kind === 'grandTotal') return EXCEL_COLORS.footerGrandTotalBg;
  return EXCEL_COLORS.totalBg;
}

export async function saveAnnualOperationPlanAsExcel(params: {
  title: string;
  metaSubtitle: string;
  scopeClassTitle?: string;
  weekdays: AnnualOperationPlanExportWeekday[];
  classHeaders: AnnualOperationPlanExportClassHeader[];
  rows: AnnualOperationPlanExportRow[];
  totalInstructionalDays: number;
  totalHoursByColumnKey: Record<string, number | string>;
  vacationText: string;
  footerRows?: Array<{
    kind: 'subtotal' | 'grandTotal';
    label: string;
    termNumber?: 1 | 2;
    totalInstructionalDays: number;
    totalHoursByColumnKey: Record<string, number | string>;
    vacationText?: string;
  }>;
  tableBodyItems?: Array<
    | { kind: 'week'; row: AnnualOperationPlanExportRow }
    | {
        kind: 'footer';
        footer: {
          kind: 'subtotal' | 'grandTotal';
          label: string;
          termNumber?: 1 | 2;
          totalInstructionalDays: number;
          totalHoursByColumnKey: Record<string, number | string>;
          vacationText?: string;
        };
      }
  >;
  fileName: string;
}): Promise<void> {
  const {
    title,
    metaSubtitle,
    scopeClassTitle,
    weekdays,
    classHeaders,
    rows,
    totalInstructionalDays,
    totalHoursByColumnKey,
    vacationText,
    footerRows,
    tableBodyItems,
    fileName,
  } = params;
  if (rows.length === 0 && (!tableBodyItems || tableBodyItems.length === 0)) return;

  const weekdayCount = weekdays.length;
  const classCount = Math.max(1, classHeaders.length);
  const columnCount = 3 + weekdayCount + classCount + 2;

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('운영계획');

  const metaLine = [scopeClassTitle, metaSubtitle].filter(Boolean).join(' · ');

  sheet.mergeCells(1, 1, 1, columnCount);
  const titleCell = sheet.getCell(1, 1);
  titleCell.value = title;
  titleCell.font = { bold: true, size: 12, color: { argb: EXCEL_COLORS.defaultText } };
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

  sheet.mergeCells(2, 1, 2, columnCount);
  const metaCell = sheet.getCell(2, 1);
  metaCell.value = metaLine;
  metaCell.font = { bold: true, size: 10, color: { argb: EXCEL_COLORS.defaultText } };
  metaCell.alignment = { horizontal: 'center', vertical: 'middle' };

  const headerRow1Index = 3;
  const headerRow2Index = 4;
  const dataStartRowIndex = 5;

  const headerRow1 = sheet.getRow(headerRow1Index);
  const headerRow2 = sheet.getRow(headerRow2Index);

  const weekdayStartCol = 4;
  const classStartCol = weekdayStartCol + weekdayCount;
  const activityCol = classStartCol + classCount;
  const remarkCol = activityCol + 1;

  headerRow1.getCell(1).value = '월';
  headerRow1.getCell(2).value = '주';
  headerRow1.getCell(3).value = '수업일수';
  headerRow1.getCell(weekdayStartCol).value = '연간 수업 일수';
  headerRow1.getCell(classStartCol).value = '클래스별 수업시수';
  headerRow1.getCell(activityCol).value = '주요 교육 활동';
  headerRow1.getCell(remarkCol).value = '교육주간 및 비고';

  sheet.mergeCells(headerRow1Index, 1, headerRow2Index, 1);
  sheet.mergeCells(headerRow1Index, 2, headerRow2Index, 2);
  sheet.mergeCells(headerRow1Index, 3, headerRow2Index, 3);
  sheet.mergeCells(headerRow1Index, weekdayStartCol, headerRow1Index, weekdayStartCol + weekdayCount - 1);
  sheet.mergeCells(headerRow1Index, classStartCol, headerRow1Index, classStartCol + classCount - 1);
  sheet.mergeCells(headerRow1Index, activityCol, headerRow2Index, activityCol);
  sheet.mergeCells(headerRow1Index, remarkCol, headerRow2Index, remarkCol);

  weekdays.forEach((weekday, index) => {
    headerRow2.getCell(weekdayStartCol + index).value = weekday.label;
  });
  classHeaders.forEach((header, index) => {
    headerRow2.getCell(classStartCol + index).value = header.gradeNumber || '-';
  });

  for (let col = 1; col <= columnCount; col += 1) {
    styleHeaderCell(headerRow1.getCell(col));
    styleHeaderCell(headerRow2.getCell(col));
  }

  const flatDataRows: string[][] = rows
    .filter((row) => !row.rowKind || row.rowKind === 'week')
    .map((row) => [
      row.monthLabel,
      String(row.weekIndex),
      String(row.instructionalDayCount),
      ...row.weekdayCells.map((day) => day.value),
      ...row.classHourCells.map((classHour) => String(classHour.value)),
      row.activityLines.map(formatActivityLinePlain).join('\n'),
      row.remarkTexts.map(formatRemarkText).join('\n'),
    ]);

  const writeWeekRow = (row: AnnualOperationPlanExportRow, excelRowIndex: number): void => {
    const excelRow = sheet.getRow(excelRowIndex);

    excelRow.getCell(1).value = row.monthLabel;
    styleBodyCell(excelRow.getCell(1), 'center');

    excelRow.getCell(2).value = row.weekIndex;
    styleBodyCell(excelRow.getCell(2), 'center');

    excelRow.getCell(3).value = row.instructionalDayCount;
    styleBodyCell(excelRow.getCell(3), 'center');
    excelRow.getCell(3).font = { bold: true, color: { argb: EXCEL_COLORS.defaultText } };
    excelRow.getCell(3).fill = solidFill(EXCEL_COLORS.highlightYellowBg);

    row.weekdayCells.forEach((day, dayIndex) => {
      const cell = excelRow.getCell(weekdayStartCol + dayIndex);
      cell.value = day.value;
      applyDayCellStyle(cell, day);
    });

    row.classHourCells.forEach((classHour, classIndex) => {
      const cell = excelRow.getCell(classStartCol + classIndex);
      cell.value = classHour.value;
      styleBodyCell(cell, 'center');
      cell.fill = solidFill(EXCEL_COLORS.highlightYellowBg);
      if (classHour.adjusted) {
        cell.font = {
          bold: true,
          color: { argb: EXCEL_COLORS.hoursAdjustedText },
          underline: true,
        };
      }
    });

    const activityCell = excelRow.getCell(activityCol);
    applyCellChrome(activityCell, 'left');
    activityCell.value = row.activityLines.length > 0 ? buildActivityRichText(row.activityLines) : '';

    const remarkCell = excelRow.getCell(remarkCol);
    remarkCell.value = row.remarkTexts.map(formatRemarkText).join('\n');
    styleBodyCell(remarkCell, 'left');
  };

  const writeFooterRow = (
    footer: {
      kind?: 'subtotal' | 'grandTotal';
      label: string;
      termNumber?: 1 | 2;
      totalInstructionalDays: number;
      totalHoursByColumnKey: Record<string, number | string>;
      vacationText?: string;
    },
    excelRowIndex: number
  ): void => {
    const totalRow = sheet.getRow(excelRowIndex);
    const footerBg = resolveFooterBackgroundArgb(footer);
    totalRow.getCell(1).value = footer.label;
    sheet.mergeCells(excelRowIndex, 1, excelRowIndex, 2);
    totalRow.getCell(3).value = footer.totalInstructionalDays;
    totalRow.getCell(weekdayStartCol).value = '수업 시수';
    sheet.mergeCells(excelRowIndex, weekdayStartCol, excelRowIndex, weekdayStartCol + weekdayCount - 1);

    classHeaders.forEach((header, index) => {
      const value =
        header.columnKey === 'none' ? '-' : String(footer.totalHoursByColumnKey[header.columnKey] ?? 0);
      totalRow.getCell(classStartCol + index).value = value;
    });
    totalRow.getCell(activityCol).value = footer.vacationText ?? '';

    for (let col = 1; col <= columnCount; col += 1) {
      const align = col >= activityCol ? 'left' : 'center';
      styleTotalCell(totalRow.getCell(col), align, footerBg);
    }
  };

  let nextRowIndex = dataStartRowIndex;
  if (tableBodyItems && tableBodyItems.length > 0) {
    tableBodyItems.forEach((item) => {
      if (item.kind === 'week') {
        writeWeekRow(item.row, nextRowIndex);
        nextRowIndex += 1;
        return;
      }
      writeFooterRow(item.footer, nextRowIndex);
      nextRowIndex += 1;
    });
  } else {
    let weekRowIndex = 0;
    rows.forEach((row) => {
      if (row.rowKind && row.rowKind !== 'week') return;
      writeWeekRow(row, dataStartRowIndex + weekRowIndex);
      weekRowIndex += 1;
    });

    const dataRowCount = weekRowIndex;
    const resolvedFooterRows =
      footerRows && footerRows.length > 0
        ? footerRows
        : [
            {
              kind: 'grandTotal' as const,
              label: '총계',
              totalInstructionalDays,
              totalHoursByColumnKey,
              vacationText,
            },
          ];

    resolvedFooterRows.forEach((footer, footerOffset) => {
      writeFooterRow(footer, dataStartRowIndex + dataRowCount + footerOffset);
    });
  }

  const widthSamples = [
    ['월', '주', '수업일수', ...weekdays.map((w) => w.label), ...classHeaders.map((h) => h.gradeNumber || '-'), '주요 교육 활동', '교육주간 및 비고'],
    ...flatDataRows,
  ];
  sheet.columns = Array.from({ length: columnCount }, (_, colIndex) => {
    const contentWch = Math.max(
      ...widthSamples.map((row) => displayWidthOfText((row[colIndex] ?? '').trim())),
      colIndex === activityCol - 1 ? 28 : colIndex === remarkCol - 1 ? 20 : 4
    );
    return { width: Math.min(56, contentWch + 2) };
  });

  headerRow1.height = 24;
  headerRow2.height = 24;

  const buffer = await workbook.xlsx.writeBuffer();
  triggerBrowserDownload(buffer, fileName);
}
